/**
 * The jump-scare's face — entirely code-drawn, an original design with no likeness of
 * any film character or real person (see `public/scare/ASSETS.md`'s content boundary).
 * The brief: a gaunt, asymmetric skull-like face with sunken cheeks; deep-set bloodshot
 * eyes with visible sclera around a single pinpoint pupil; individual, irregular teeth
 * that read as a broken jawline (some simply missing) rather than a uniform zigzag; wet
 * specular highlights on the skin, teeth and eyes; and a hard rim light along one edge
 * against the scare overlay's own near-black background.
 *
 * `t` is elapsed seconds since the scare started. It only drives a small tremor/pulse on
 * top of the otherwise-static geometry — `ScareScene` calls this once with `t = 0` for
 * its reduced-motion static frame, and that frame needs to look like a held breath, not
 * a paused animation.
 */
export function drawFace(ctx: CanvasRenderingContext2D, w: number, h: number, t: number): void {
  const cx = w / 2;
  const cy = h / 2;
  // Averaging both dimensions — not just the smaller one, as the previous design did —
  // keeps the face large and screen-filling on any aspect ratio: a portrait phone
  // screen and an ultra-wide stream capture both get a dominant subject.
  const scale = (w + h) / 2;
  const faceW = scale * 0.3;
  const faceH = faceW * 1.85;
  const tremor = Math.sin(t * 14) * scale * 0.002;
  const asymmetry = scale * 0.02;

  ctx.save();
  ctx.translate(cx + tremor, cy);

  drawSkull(ctx, faceW, faceH, asymmetry);
  drawRimLight(ctx, faceW, faceH, asymmetry);
  drawVeins(ctx, faceW, faceH, t);
  drawEyes(ctx, faceW, faceH, asymmetry, t);
  drawMouth(ctx, faceW, faceH, asymmetry, t);

  ctx.restore();
}

function drawSkull(
  ctx: CanvasRenderingContext2D,
  faceW: number,
  faceH: number,
  asymmetry: number,
): void {
  const gradient = ctx.createRadialGradient(
    -faceW * 0.1,
    -faceH * 0.15,
    faceW * 0.1,
    0,
    0,
    faceH * 0.82,
  );
  gradient.addColorStop(0, "#ded4c2");
  gradient.addColorStop(0.42, "#b7ab97");
  gradient.addColorStop(0.78, "#6b6155");
  gradient.addColorStop(1, "#201b17");

  // A gaunt, sunken-cheek silhouette: the left half pinches inward hard past the
  // temple before flaring back out for the jaw, and travels further out than the
  // right half — the asymmetry the brief calls for, not a mirrored ellipse.
  ctx.beginPath();
  ctx.moveTo(0, -faceH * 0.54);
  ctx.bezierCurveTo(
    -faceW * 0.85,
    -faceH * 0.5,
    -faceW * 1.08 - asymmetry,
    -faceH * 0.04,
    -faceW * 0.6,
    faceH * 0.2,
  );
  ctx.bezierCurveTo(
    -faceW * 0.46,
    faceH * 0.36,
    -faceW * 0.6,
    faceH * 0.44,
    -faceW * 0.4,
    faceH * 0.57,
  );
  ctx.quadraticCurveTo(0, faceH * 0.65, faceW * 0.38, faceH * 0.55);
  ctx.bezierCurveTo(
    faceW * 0.56,
    faceH * 0.4,
    faceW * 0.46,
    faceH * 0.3,
    faceW * 0.56,
    faceH * 0.15,
  );
  ctx.bezierCurveTo(
    faceW * 1.02 + asymmetry * 0.6,
    -faceH * 0.03,
    faceW * 0.8,
    -faceH * 0.52,
    0,
    -faceH * 0.54,
  );
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();

  ctx.lineWidth = Math.max(1, faceW * 0.015);
  ctx.strokeStyle = "rgba(18, 14, 11, 0.6)";
  ctx.stroke();

  // Sunken-cheek shading — soft dark ellipses under each cheekbone sell "gaunt" even
  // where the silhouette itself already pinches inward there.
  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = "#171310";
  ctx.beginPath();
  ctx.ellipse(-faceW * 0.42, faceH * 0.09, faceW * 0.17, faceH * 0.14, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(
    faceW * 0.4 + asymmetry * 0.3,
    faceH * 0.07,
    faceW * 0.14,
    faceH * 0.12,
    0.3,
    0,
    Math.PI * 2,
  );
  ctx.fill();
  ctx.restore();

  // A couple of wet specular flecks on the brow/cheekbone — "wet specular highlights"
  // aren't limited to the eyes and teeth.
  ctx.save();
  ctx.fillStyle = "rgba(255, 255, 255, 0.35)";
  ctx.beginPath();
  ctx.ellipse(-faceW * 0.5, -faceH * 0.28, faceW * 0.05, faceH * 0.02, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(faceW * 0.3, faceH * 0.32, faceW * 0.03, faceH * 0.015, 0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A hard, thin rim light traced along the skull's LEFT (stage-left-lit) edge only —
 * against the scare overlay's own near-black background this reads as a single strong
 * directional light source, not the old design's flat, all-around dark stroke. */
function drawRimLight(
  ctx: CanvasRenderingContext2D,
  faceW: number,
  faceH: number,
  asymmetry: number,
): void {
  ctx.save();
  ctx.shadowColor = "rgba(255, 248, 225, 0.9)";
  ctx.shadowBlur = Math.max(2, faceW * 0.05);
  ctx.strokeStyle = "rgba(255, 250, 235, 0.85)";
  ctx.lineWidth = Math.max(1, faceW * 0.012);
  ctx.beginPath();
  ctx.moveTo(-faceW * 0.05, -faceH * 0.53);
  ctx.bezierCurveTo(
    -faceW * 0.85,
    -faceH * 0.5,
    -faceW * 1.08 - asymmetry,
    -faceH * 0.04,
    -faceW * 0.6,
    faceH * 0.2,
  );
  ctx.bezierCurveTo(
    -faceW * 0.46,
    faceH * 0.36,
    -faceW * 0.6,
    faceH * 0.44,
    -faceW * 0.4,
    faceH * 0.57,
  );
  ctx.stroke();
  ctx.restore();
}

type VeinPath = readonly [x0: number, y0: number, cx: number, cy: number, x1: number, y1: number];

function drawVeins(ctx: CanvasRenderingContext2D, faceW: number, faceH: number, t: number): void {
  const pulse = 0.25 + 0.15 * Math.sin(t * 6);
  ctx.strokeStyle = `rgba(120, 20, 30, ${pulse.toFixed(3)})`;
  ctx.lineWidth = Math.max(1, faceW * 0.012);

  const veins: readonly VeinPath[] = [
    [-faceW * 0.5, -faceH * 0.3, -faceW * 0.65, 0, -faceW * 0.4, faceH * 0.35],
    [faceW * 0.45, -faceH * 0.35, faceW * 0.7, -faceH * 0.05, faceW * 0.5, faceH * 0.3],
    [-faceW * 0.1, -faceH * 0.5, -faceW * 0.02, -faceH * 0.15, -faceW * 0.15, faceH * 0.05],
    [faceW * 0.15, -faceH * 0.48, faceW * 0.22, -faceH * 0.1, faceW * 0.08, faceH * 0.08],
  ];

  for (const [x0, y0, cx, cy, x1, y1] of veins) {
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.quadraticCurveTo(cx, cy, x1, y1);
    ctx.stroke();
  }
}

interface EyeSpec {
  readonly x: number;
  readonly rx: number;
  readonly ry: number;
}

/** Deep-set sockets around bloodshot sclera and a single pinpoint pupil — the previous
 * design's eyes were pure black ellipses with no sclera/veining at all. */
function drawEyes(
  ctx: CanvasRenderingContext2D,
  faceW: number,
  faceH: number,
  asymmetry: number,
  t: number,
): void {
  const eyeY = -faceH * 0.1;
  const eyes: readonly EyeSpec[] = [
    { x: -faceW * 0.38, rx: faceW * 0.2, ry: faceH * 0.12 },
    { x: faceW * 0.42 + asymmetry * 0.4, rx: faceW * 0.17, ry: faceH * 0.1 },
  ];

  for (const eye of eyes) {
    // Deep-set socket: a soft dark halo behind the eye itself.
    ctx.beginPath();
    ctx.ellipse(eye.x, eyeY, eye.rx * 1.35, eye.ry * 1.35, 0, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(8, 6, 6, 0.75)";
    ctx.fill();

    // Sclera — off-white (sits in shadow), not pure white, so it doesn't blow out
    // against the rim light.
    ctx.beginPath();
    ctx.ellipse(eye.x, eyeY, eye.rx, eye.ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = "#e7ded0";
    ctx.fill();

    // Bloodshot veining across the sclera — a fixed spread of curved capillaries.
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(eye.x, eyeY, eye.rx, eye.ry, 0, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = "rgba(160, 20, 20, 0.55)";
    ctx.lineWidth = Math.max(0.6, eye.rx * 0.02);
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const sx = eye.x + Math.cos(angle) * eye.rx * 0.1;
      const sy = eyeY + Math.sin(angle) * eye.ry * 0.1;
      const mx = eye.x + Math.cos(angle) * eye.rx * 0.55;
      const my = eyeY + Math.sin(angle) * eye.ry * 0.55;
      const ex = eye.x + Math.cos(angle) * eye.rx * 1.05;
      const ey = eyeY + Math.sin(angle) * eye.ry * 1.05;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.quadraticCurveTo(mx, my, ex, ey);
      ctx.stroke();
    }
    ctx.restore();

    // Pinpoint pupil, faintly jittering.
    const jitter = Math.sin(t * 9 + eye.x) * eye.rx * 0.06;
    ctx.beginPath();
    ctx.arc(eye.x + jitter, eyeY + eye.ry * 0.08, Math.max(1, faceW * 0.016), 0, Math.PI * 2);
    ctx.fillStyle = "#070503";
    ctx.fill();

    // A tiny wet specular highlight — sells "wet" on the eye itself.
    ctx.beginPath();
    ctx.arc(
      eye.x + jitter - eye.rx * 0.06,
      eyeY + eye.ry * 0.02,
      Math.max(0.6, faceW * 0.006),
      0,
      Math.PI * 2,
    );
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.fill();
  }
}

/** A deterministic pseudo-random hash in [0,1) for `i` — the classic GLSL-style
 * `sin`-based trick. Deterministic per index (not per-frame) so which teeth read as
 * "missing" doesn't flicker across animation frames. */
function hash01(i: number): number {
  const v = Math.sin(i * 12.9898) * 43758.5453;
  return Math.abs(v - Math.floor(v));
}

/** Individual, irregular teeth — some missing entirely — instead of a uniform zigzag,
 * so the jawline reads as broken rather than merely serrated; each tooth also gets a
 * small wet specular fleck. */
function drawMouth(
  ctx: CanvasRenderingContext2D,
  faceW: number,
  faceH: number,
  asymmetry: number,
  t: number,
): void {
  const mouthY = faceH * 0.36;
  const mouthW = faceW * (1.2 + Math.sin(t * 20) * 0.02);
  const left = -mouthW / 2 - asymmetry * 0.3;
  const right = mouthW / 2 + asymmetry * 0.5;
  const cavityBottom = mouthY + faceH * 0.2;

  ctx.beginPath();
  ctx.moveTo(left, mouthY);
  ctx.quadraticCurveTo(0, mouthY + faceH * 0.02, right, mouthY);
  ctx.quadraticCurveTo(right * 0.9, mouthY + faceH * 0.24, 0, cavityBottom);
  ctx.quadraticCurveTo(left * 0.9, mouthY + faceH * 0.24, left, mouthY);
  ctx.closePath();
  ctx.fillStyle = "#180404";
  ctx.fill();

  const toothCount = 9;
  const toothW = (right - left) / toothCount;
  for (let i = 0; i < toothCount; i++) {
    const tx = left + toothW * (i + 0.5);
    const upperSeed = hash01(i);
    const lowerSeed = hash01(i + 100);
    const w = toothW * (0.55 + upperSeed * 0.25);

    if (upperSeed <= 0.82) {
      const len = faceH * (0.05 + upperSeed * 0.05);
      drawTooth(ctx, tx, mouthY + faceH * 0.01, w, len, "down", "#efe6d4", faceW);
    }
    if (lowerSeed <= 0.85) {
      const len = faceH * (0.04 + lowerSeed * 0.045);
      drawTooth(ctx, tx, cavityBottom - faceH * 0.01, w, len, "up", "#e3d8c2", faceW);
    }
  }

  ctx.strokeStyle = "rgba(90, 20, 20, 0.6)";
  ctx.lineWidth = Math.max(1, faceW * 0.01);
  ctx.beginPath();
  ctx.moveTo(left, mouthY);
  ctx.quadraticCurveTo(0, mouthY + faceH * 0.02, right, mouthY);
  ctx.stroke();
}

/** A single tapered tooth hanging (`dir: "down"`) from the upper gumline or rising
 * (`dir: "up"`) from the lower — plus a small wet specular fleck near its base. */
function drawTooth(
  ctx: CanvasRenderingContext2D,
  tx: number,
  baseY: number,
  w: number,
  len: number,
  dir: "down" | "up",
  fill: string,
  faceW: number,
): void {
  const tipY = dir === "down" ? baseY + len : baseY - len;
  ctx.beginPath();
  ctx.moveTo(tx - w / 2, baseY);
  ctx.lineTo(tx + w / 2, baseY);
  ctx.lineTo(tx + w * 0.3, tipY);
  ctx.lineTo(tx - w * 0.3, tipY);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = "rgba(40, 30, 20, 0.5)";
  ctx.lineWidth = Math.max(0.5, faceW * 0.004);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(
    tx - w * 0.1,
    baseY + (dir === "down" ? len * 0.15 : -len * 0.15),
    Math.max(0.5, w * 0.12),
    0,
    Math.PI * 2,
  );
  ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
  ctx.fill();
}
